import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, QrCode, Eye, Trash2, ExternalLink } from "lucide-react";
import QrModal from "@/components/QrModal";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [qrProduct, setQrProduct] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/products", {
        params: { q, status: statusFilter || undefined },
      });
      if (!cancelled) setProducts(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [q, statusFilter, tick]);

  const del = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    await api.delete(`/products/${id}`);
    setTick((t) => t + 1);
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6 md:mb-8">
        <div className="min-w-0">
          <div className="hed-overline">Catalog</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">Products</h1>
        </div>
        <Link
          to="/admin/products/new"
          className="inline-flex items-center gap-2 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-4 py-2.5 md:px-5 md:py-3 rounded-sm font-medium transition-colors text-sm"
          data-testid="products-new-button"
        >
          <Plus size={16} strokeWidth={2} /> New product
        </Link>
      </div>

      {/* Search + filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 border-b border-[#E8E6DF] focus-within:border-[#B5953B] py-2 flex-1 min-w-[220px]">
          <Search size={16} strokeWidth={1.5} className="text-[#5C5C54]" />
          <input
            data-testid="products-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, code, category, material..."
            className="bg-transparent flex-1 outline-none text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          {["", "draft", "published"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s || "all"}-button`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                statusFilter === s
                  ? "bg-[#1A1A18] text-white border-[#1A1A18]"
                  : "border-[#E8E6DF] text-[#5C5C54] hover:bg-[#F2F0EA]"
              }`}
            >
              {s ? s.toUpperCase() : "ALL"}
            </button>
          ))}
        </div>
      </div>

      <div className="hed-card overflow-x-auto">
        <table className="w-full min-w-[480px]">
          <thead className="bg-[#FAFAF8] border-b border-[#E8E6DF]">
            <tr>
              <th className="text-left hed-overline px-5 py-3" style={{ fontSize: 10 }}>Product</th>
              <th className="text-left hed-overline px-5 py-3 hidden md:table-cell" style={{ fontSize: 10 }}>Code</th>
              <th className="text-left hed-overline px-5 py-3 hidden md:table-cell" style={{ fontSize: 10 }}>Category</th>
              <th className="text-left hed-overline px-5 py-3" style={{ fontSize: 10 }}>Status</th>
              <th className="text-right hed-overline px-5 py-3" style={{ fontSize: 10 }}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E6DF]">
            {products.map((p) => (
              <tr key={p.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`product-row-${p.id}`}>
                <td className="px-5 py-3">
                  <button
                    onClick={() => nav(`/admin/products/${p.id}/edit`)}
                    className="flex items-center gap-3 text-left"
                  >
                    <div className="w-10 h-10 bg-[#F2F0EA] border border-[#E8E6DF] rounded-sm overflow-hidden flex items-center justify-center">
                      {p.images?.[0] ? (
                        <img src={fileUrl(p.images[0].storage_path)} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <span className="text-xs text-[#5E6033]">—</span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{p.name || "Untitled draft"}</div>
                      <div className="text-xs text-[#5C5C54]">{p.views || 0} views</div>
                    </div>
                  </button>
                </td>
                <td className="px-5 py-3 hidden md:table-cell text-sm font-mono text-[#5C5C54]">{p.code || "—"}</td>
                <td className="px-5 py-3 hidden md:table-cell text-sm">{p.category || "—"}</td>
                <td className="px-5 py-3">
                  <span
                    className={`text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-sm ${
                      p.status === "published"
                        ? "bg-[#3A5A40]/10 text-[#3A5A40]"
                        : "bg-[#F2F0EA] text-[#5E6033]"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {p.status === "published" && p.code && (
                      <>
                        <button
                          onClick={() => setQrProduct(p)}
                          className="p-2 hover:bg-[#F2F0EA] rounded-sm"
                          title="QR code"
                          data-testid={`product-qr-button-${p.id}`}
                        >
                          <QrCode size={16} strokeWidth={1.5} />
                        </button>
                        <a
                          href={`/product/${p.code}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 hover:bg-[#F2F0EA] rounded-sm"
                          title="Open public page"
                        >
                          <ExternalLink size={16} strokeWidth={1.5} />
                        </a>
                      </>
                    )}
                    <button
                      onClick={() => nav(`/admin/products/${p.id}/edit`)}
                      className="p-2 hover:bg-[#F2F0EA] rounded-sm"
                      title="Edit"
                      data-testid={`product-edit-button-${p.id}`}
                    >
                      <Eye size={16} strokeWidth={1.5} />
                    </button>
                    {user?.role === "owner" && (
                      <button
                        onClick={() => del(p.id)}
                        className="p-2 hover:bg-[#9E2A2B]/10 hover:text-[#9E2A2B] rounded-sm"
                        title="Delete"
                        data-testid={`product-delete-button-${p.id}`}
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center text-sm text-[#5C5C54]">
                  No products yet. Click &ldquo;New product&rdquo; to upload your first brochure.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {qrProduct && <QrModal product={qrProduct} onClose={() => setQrProduct(null)} />}
    </AdminLayout>
  );
}
